/**
 * Copyright 2026 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { useState } from 'react';
import { getYouTubeEmbedUrl, getYouTubeThumbnailUrl } from '@/utils/youtubeUrl';
import { useLocale } from '@/hooks/useLocale';
import styles from './YouTubeFacade.module.scss';

const YOUTUBE_IFRAME_ALLOW =
  'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';

const BASE_EMBED_PARAMS = { autoplay: 1, rel: 0, modestbranding: 1 } as const;

const PLAY_ICON_URL = 'https://www.autozone.com/cdn/icons/az_icons/youtube-play.svg';

export type YouTubeFacadeProps = {
  /** Original YouTube URL (watch, youtu.be, or embed form). */
  videoUrl: string;
  /** Accessible label for the play button and iframe title. */
  title: string;
  /** Optional poster image override. Falls back to YouTube's CDN thumbnail. */
  posterImage?: string;
};

/**
 * Renders a lightweight placeholder ("facade") for a YouTube video. The actual
 * YouTube iframe — and its ~500KB of JS/CSS — is not loaded until the user
 * clicks the facade. Once activated, the iframe uses `autoplay=1` so the user
 * does not have to click play a second time.
 */
export function YouTubeFacade({ videoUrl, title, posterImage }: YouTubeFacadeProps) {
  const [isActivated, setIsActivated] = useState(false);
  const locale = useLocale();

  const youtubeThumbnail = getYouTubeThumbnailUrl(videoUrl);
  // Treat empty/whitespace-only posterImage (a common ContentStack default) as
  // missing so we fall through to the YouTube CDN thumbnail.
  const trimmedPoster = posterImage?.trim();
  const initialThumbnail = trimmedPoster ? trimmedPoster : youtubeThumbnail;
  const [thumbnailSrc, setThumbnailSrc] = useState<string | undefined>(initialThumbnail);

  if (isActivated) {
    const embedParams = { ...BASE_EMBED_PARAMS, hl: locale };
    return (
      <iframe
        src={getYouTubeEmbedUrl(videoUrl, embedParams)}
        title={title}
        className={styles.iframe}
        allow={YOUTUBE_IFRAME_ALLOW}
        allowFullScreen
      />
    );
  }

  const handleThumbnailError = () => {
    // If a custom posterImage failed to load, fall back to YouTube's CDN.
    if (youtubeThumbnail && thumbnailSrc !== youtubeThumbnail) {
      setThumbnailSrc(youtubeThumbnail);
    }
  };

  return (
    <button
      type="button"
      onClick={() => setIsActivated(true)}
      className={styles.facade}
      aria-label={`Play video: ${title}`}
    >
      {thumbnailSrc ? (
        <img
          src={thumbnailSrc}
          alt=""
          className={styles.thumbnail}
          onError={handleThumbnailError}
        />
      ) : null}
      <img src={PLAY_ICON_URL} alt="" className={styles.playButton} aria-hidden="true" />
    </button>
  );
}
